import { FormApi } from "final-form";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { TextField } from "src/ui/shared/form/TextField";
import { Modal } from "src/ui/shared/Modal/Modal";

import { useUpdatePolicyMutation } from "@dashboard/policies/api";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { policyNameExists } from "../../PoliciesPage.utils";

type FormValues = {
    name: string;
};

type Props = {
    policy: API.PolicyViewModel;
    policies: API.PolicyViewModel[];
    open: boolean;
    onClose: () => void;
};

const validate = (values: FormValues, policy: API.PolicyViewModel, policies: API.PolicyViewModel[]) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};
    if (!values.name?.trim()) {
        errors.name = i18next.t("modals:createPolicy.input.name.validation.required");
    } else if (
        policyNameExists(
            policies.filter((item) => item.id !== policy.id),
            values.name,
        )
    ) {
        errors.name = i18next.t("modals:createPolicy.input.name.validation.exists");
    }
    return errors;
};

const EditPolicyModal = (props: Props) => {
    const { t } = useTranslation(["modals", "common"]);

    const [updatePolicy, { isLoading }] = useUpdatePolicyMutation();

    const onSubmit = async (values: FormValues) => {
        try {
            const content = props.policy.content ?? [];
            await updatePolicy({ id: props.policy.id, name: values.name.trim(), content }).unwrap();
            showSuccessToast(t("modals:editPolicy.toast.editSuccess"));
            props.onClose();
        } catch {
            // The policies API's transformErrorResponse already shows an error toast for the failed
            // request, so the modal is simply left open here for the user to retry.
        }
    };

    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        if (e.key === "Enter" && !form.getState().submitting) {
            e.preventDefault();
            await form.submit();
        }
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={(values) => validate(values, props.policy, props.policies)}
            initialValues={{ name: props.policy.name }}
            render={({ handleSubmit, form }) => (
                <form onSubmit={handleSubmit}>
                    <Modal
                        open={props.open}
                        onClose={props.onClose}
                        title={t("modals:editPolicy.title")}
                        onKeyDown={(e) => onKeyDown(e, form)}
                        endButton={
                            <PrimaryButton onClick={() => form.submit()} disabled={isLoading} loading={isLoading}>
                                {t("common:button.save")}
                            </PrimaryButton>
                        }
                    >
                        <TextField name="name" label={t("common:label.name")} fullWidth required autoFocus />
                    </Modal>
                </form>
            )}
        />
    );
};

export default EditPolicyModal;
